import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks';
import { Construction, ArrowLeft, Clock, Wrench, Code, Sparkles } from 'lucide-react';

interface UnderConstructionProps {
  /**
   * Optional page title override
   */
  pageTitle?: string;
  /**
   * Show back button (default: true)
   */
  showBackButton?: boolean;
  /**
   * Show estimated time (default: true)
   */
  showEstimatedTime?: boolean;
  /**
   * Show feature list (default: true)
   */
  showFeatures?: boolean;
  /**
   * Custom features list to override default translations
   */
  customFeatures?: string[];
  /**
   * Additional custom message
   */
  customMessage?: string;
}

const UnderConstruction = ({
  pageTitle,
  showBackButton = true,
  showEstimatedTime = true,
  showFeatures = true,
  customFeatures,
  customMessage
}: UnderConstructionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/');
  };

  const features = customFeatures || t.underConstruction.features.items;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full text-center">
        <div className="mb-8">
          <div className="mx-auto w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
            <Construction className="w-12 h-12 text-yellow-600" />
          </div>
          <div className="flex justify-center space-x-4 mb-4">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-bounce"></div>
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          {pageTitle || t.underConstruction.title}
        </h1>

        <p className="text-xl text-gray-600 mb-6">
          {t.underConstruction.subtitle}
        </p>

        <p className="text-gray-500 mb-8 leading-relaxed">
          {t.underConstruction.description}
        </p>

        {/* Custom Message */}
        {customMessage && (
          <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-700">{customMessage}</p>
        </div>
        )}

        {/* Features List */}
        {showFeatures && (
          <div className="mb-8 bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-center">
              <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
              {t.underConstruction.features.title}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center text-left">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    {index === 0 && <Code className="w-3 h-3 text-green-600" />}
                    {index === 1 && <Sparkles className="w-3 h-3 text-green-600" />}
                    {index === 2 && <Wrench className="w-3 h-3 text-green-600" />}
                    {index >= 3 && <div className="w-2 h-2 bg-green-600 rounded-full"></div>}
                  </div>
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Estimated Time */}
        {showEstimatedTime && (
          <div className="mb-8 flex items-center justify-center text-gray-500">
            <Clock className="w-5 h-5 mr-2" />
            <span>{t.underConstruction.estimatedTime}</span>
          </div>
        )}

        {/* Contact Info */}
        <p className="text-gray-400 mb-8 italic">
          {t.underConstruction.contactInfo}
        </p>

        {/* Back Button */}
        {showBackButton && (
          <button
            onClick={handleBackClick}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t.underConstruction.backButton}
          </button>
        )}
      </div>
    </div>
  );
};

export default UnderConstruction;
